import { emit, on, getState } from "../../core/state.js";
import { getUiFeature } from "../../core/configAccessors.js";
import wineEmptyIcon from "../../assets/icons/wine-empty.svg";
import wineFairIcon from "../../assets/icons/wine-fair.svg";
import wineGoodIcon from "../../assets/icons/wine-good.svg";
import wineGreatIcon from "../../assets/icons/wine-great.svg";
import wineGlassesIcon from "../../assets/icons/wine-glasses.svg";
import redAugmentLogo from "../../assets/images/red-augment-logo.png";
import { brandWordmarkHtml } from "./BrandWordmark.js";

export default class Footer {
  render({ panelEl }) {
    const footer = document.createElement("div");
    footer.className = "ws-footer";

    const left = document.createElement("div");
    left.className = "ws-footer-left";

    const poweredText = document.createElement("span");
    poweredText.className = "ws-powered-text";
    poweredText.textContent = "Powered by";
    left.appendChild(poweredText);

    const brandLink = document.createElement("a");
    brandLink.className = "ws-powered-link";
    brandLink.href = "https://www.redaugment.com/products/redconcierge";
    brandLink.target = "_blank";
    brandLink.rel = "noopener noreferrer";

    const logo = document.createElement("img");
    logo.src = redAugmentLogo;
    logo.alt = "Red Augment";
    logo.className = "ws-redaugment-logo";

    const brand = document.createElement("span");
    brand.className = "ws-powered-brand";
    brand.innerHTML = brandWordmarkHtml;
    brandLink.appendChild(brand);
    brandLink.appendChild(logo);

    left.appendChild(brandLink);
    footer.appendChild(left);

    const showFeedback = getUiFeature("show_feedback", true);

    if (showFeedback) {
      const sep = document.createElement("span");
      sep.className = "ws-footer-sep";
      footer.appendChild(sep);
    }

    const right = document.createElement("div");
    right.className = "ws-footer-right";

    if (showFeedback) {
      const fb = document.createElement("button");
      fb.className = "ws-feedback-btn";
      fb.type = "button";

      const label = document.createElement("span");
      label.className = "ws-feedback-label";
      label.textContent = "Feedback";
      fb.appendChild(label);

      const glasses = document.createElement("span");
      glasses.className = "ws-feedback-glasses";
      glasses.innerHTML = wineGlassesIcon;
      fb.appendChild(glasses);

      fb.addEventListener("click", () => this._openFeedbackModal(panelEl));
      right.appendChild(fb);

      // Parity: after the 4th user message, auto-show a one-time nudge inviting
      // feedback (old trackUserMessages/showFeedbackPopup, script.js 490-522).
      let nudged = false;
      on("ws-message-added", () => {
        if (nudged) return;
        const users = (getState().messages || []).filter((m) => m.role === "user").length;
        if (users >= 4) {
          nudged = true;
          this._openFeedbackNudge(panelEl);
        }
      });
    }

    footer.appendChild(right);
    return footer;
  }

  // One-time "Enjoying the conversation?" nudge → opens the full feedback modal.
  _openFeedbackNudge(panelEl) {
    if (!panelEl || panelEl.querySelector(".ws-feedback-modal")) return;

    const modal = document.createElement("div");
    modal.className = "ws-feedback-modal";

    const dialog = document.createElement("div");
    dialog.className = "ws-feedback-dialog ws-fb-nudge";
    dialog.innerHTML = `
      <div class="ws-fb-header">
        <h5 class="ws-fb-title">Enjoying the conversation?</h5>
        <button type="button" class="ws-fb-close" title="Close">&times;</button>
      </div>
      <p class="ws-fb-desc">We'd love to hear your thoughts! Click the Feedback label below to share your experience with our AI sommelier.</p>
      <p class="ws-fb-rate-label">Look for the wine glasses at the bottom</p>
      <button type="button" class="ws-fb-submit ws-fb-nudge-cta">Share feedback now</button>
    `;

    const close = () => modal.remove();
    dialog.querySelector(".ws-fb-close").addEventListener("click", close);
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });
    dialog.querySelector(".ws-fb-nudge-cta").addEventListener("click", () => {
      close();
      this._openFeedbackModal(panelEl);
    });

    modal.appendChild(dialog);
    panelEl.appendChild(modal);
  }

  _openFeedbackModal(panelEl) {
    if (!panelEl || panelEl.querySelector(".ws-feedback-modal")) return;

    const modal = document.createElement("div");
    modal.className = "ws-feedback-modal";

    const dialog = document.createElement("div");
    dialog.className = "ws-feedback-dialog";

    let rating = 0;

    dialog.innerHTML = `
      <div class="ws-fb-header">
        <h5 class="ws-fb-title">Tell us how we did</h5>
        <button type="button" class="ws-fb-close" title="Close">&times;</button>
      </div>
      <p class="ws-fb-desc">Your feedback helps us improve your experience.</p>
      <p class="ws-fb-rate-label">Rate your experience</p>
      <div class="ws-fb-glasses">
        ${[
          { n: 1, label: "Fair" },
          { n: 2, label: "Good" },
          { n: 3, label: "Great" },
        ]
          .map(
            ({ n, label }) =>
              `<button type="button" class="ws-fb-glass" data-value="${n}">
                 <span class="ws-fb-glass-icon">${wineEmptyIcon}</span>
                 <span class="ws-fb-glass-label">${label}</span>
               </button>`
          )
          .join("")}
      </div>
      <textarea class="ws-fb-textarea" placeholder="What did you enjoy, and what could we do better?"></textarea>
      <input type="email" class="ws-fb-email" placeholder="Email (optional, if you would like a response)">
      <small class="ws-fb-error" data-for="rating">* Kindly fill the glasses!</small>
      <small class="ws-fb-error" data-for="email">* Please use a valid email address!</small>
      <button type="button" class="ws-fb-submit">Share feedback</button>
      <div class="ws-fb-done" hidden>
        <p>Your feedback has been received</p>
        <small>Thank you for sharing your thoughts</small>
      </div>
    `;

    const close = () => modal.remove();

    dialog.querySelector(".ws-fb-close").addEventListener("click", close);
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });

    const glasses = dialog.querySelectorAll(".ws-fb-glass");
    glasses.forEach((g) => {
      g.addEventListener("click", () => {
        rating = parseInt(g.dataset.value, 10);
        const filledByPosition = [wineFairIcon, wineGoodIcon, wineGreatIcon];
        glasses.forEach((gg, i) => {
          const icon = gg.querySelector(".ws-fb-glass-icon");
          icon.innerHTML = i < rating ? filledByPosition[i] : wineEmptyIcon;
        });
        dialog.querySelector('[data-for="rating"]').style.display = "none";
      });
    });

    dialog.querySelector(".ws-fb-submit").addEventListener("click", () => {
      const email = dialog.querySelector(".ws-fb-email").value.trim();
      const text = dialog.querySelector(".ws-fb-textarea").value.trim();
      const ratingErr = dialog.querySelector('[data-for="rating"]');
      const emailErr = dialog.querySelector('[data-for="email"]');

      ratingErr.style.display = "none";
      emailErr.style.display = "none";

      if (!rating) {
        ratingErr.style.display = "block";
        return;
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailErr.style.display = "block";
        return;
      }

      emit("ws-feedback-submit", { rating, text, email });

      dialog
        .querySelectorAll(
          ".ws-fb-header, .ws-fb-desc, .ws-fb-rate-label, .ws-fb-glasses, .ws-fb-textarea, .ws-fb-email, .ws-fb-error, .ws-fb-submit"
        )
        .forEach((el) => (el.style.display = "none"));
      dialog.querySelector(".ws-fb-done").hidden = false;
      setTimeout(close, 1800);
    });

    modal.appendChild(dialog);
    panelEl.appendChild(modal);
  }
}
